import { useState, useEffect, useMemo } from "react";

import {
  fetchStoreTypes,
  createStoreType,
  updateStoreType,
  deleteStoreType,
} from "../../../api/admin/coreBasics";

import { swalSuccess, swalError, swalConfirm } from "../../../utils/swalAlert.js";
import EditModal from "../components/EditModal.jsx";

import "../../../styles/AdminPanel/CoreBasic.css"

const EMPTY_FORM = { title: "", description: "" };
const PAGE_SIZE = 8;

export default function StoreTypes() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);

  const load = async () => {
    const res = await fetchStoreTypes();
    setItems(res.data);
  };

  useEffect(() => {
    load();
  }, []);

  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const paged = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, pageSafe]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ title: item.title, description: item.description });
    setIsModalOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      swalError({ title: "عنوان الزامی است" });
      return;
    }

    const payload = { title: form.title, description: form.description };

    try {
      if (editing) {
        await updateStoreType(editing.type_id, payload);
      } else {
        await createStoreType(payload);
      }

      await swalSuccess({ title: "با موفقیت ذخیره شد" });

      setIsModalOpen(false);
      load();
    } catch {
      swalError({ title: "خطا در ذخیره" });
    }
  };

  const remove = async (id) => {
    const confirmed = await swalConfirm({
      title: "حذف نوع فروشگاه؟",
      text: "این عملیات غیر قابل بازگشت است",
      confirmText: "بله حذف شود",
      danger: true,
    });

    if (!confirmed) return;

    try {
      await deleteStoreType(id);
      await swalSuccess({ title: "حذف شد" });
      load();
    } catch {
      swalError({ title: "خطا در حذف" });
    }
  };

  return (
    <>
      <div className="page-header-row">
        <h2 className="page-title">نوع فروشگاه‌ها</h2>

        <div className="add-button-wrapper">
          <button className="btn-glass" onClick={openAdd}>
            افزودن نوع فروشگاه
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>#</th>
              <th>عنوان</th>
              <th>توضیحات</th>
              <th>عملیات</th>
            </tr>
          </thead>

          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center" }}>
                  داده‌ای وجود ندارد
                </td>
              </tr>
            ) : (
              paged.map((it, i) => (
                <tr key={it.type_id}>
                  <td>{(pageSafe - 1) * PAGE_SIZE + i + 1}</td>
                  <td>{it.title}</td>
                  <td>{it.description}</td>

                  <td className="actions-cell">
                    <button
                      className="btn-icon btn-icon-edit"
                      title="ویرایش"
                      onClick={() => openEdit(it)}
                    >
                      <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                    </button>

                    <button
                      className="btn-icon btn-icon-delete"
                      title="حذف"
                      onClick={() => remove(it.type_id)}
                    >
                      <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" />
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalCount > 0 && (
          <div className="orders-pagination">
            <span className="pagination-info">
              {totalCount.toLocaleString("fa-IR")} نوع فروشگاه — صفحه{" "}
              {pageSafe.toLocaleString("fa-IR")} از{" "}
              {totalPages.toLocaleString("fa-IR")}
            </span>
            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={pageSafe === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`page-btn number${n === pageSafe ? " active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {n.toLocaleString("fa-IR")}
                </button>
              ))}
              <button
                className="page-btn"
                disabled={pageSafe === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <EditModal onClose={() => setIsModalOpen(false)}>
          <h3>{editing ? "ویرایش نوع فروشگاه" : "افزودن نوع فروشگاه"}</h3>

          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="عنوان"
          />

          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="توضیحات"
            rows={4}
          />

          <div className="modal-buttons">
            <button className="btn" onClick={save}>
              ذخیره
            </button>

            <button
              className="btn btn-cancel"
              onClick={() => setIsModalOpen(false)}
            >
              انصراف
            </button>
          </div>
        </EditModal>
      )}
    </>
  );
}
